import { SubscriptionTier } from '@wwe-portal/entities/subscription.type';
import { useMemo } from 'react';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { NotEnoughClubCoinDialog } from '@wwe-portal/web/components/club-store/modals/NotEnoughClubCoinDialog';
import { ReferSubscriptionDialog } from '@wwe-portal/web/components/club-store/modals/refer-subscription-dialog';
import { isBetterTier } from '@wwe-portal/web/utils/subscription/comparer';
import { showDialog } from '@wwe-portal/ui/components/dialog';
import { subscriptionMap } from '@wwe-portal/ui/features/club-store';
import { useIsAuthenticated } from '@wwe-portal/web/providers/stores/auth-store';
import { useSubscriptionQuery } from '@wwe-portal/web/providers/queries/subscription';
import { useGetClubCoins } from '@wwe-portal/web/providers/queries/currency';
import { useExclusiveItems } from '@wwe-portal/web/providers/queries/club-store';
import { useTranslations } from 'next-intl';
import { LoginDialogPortable } from '@wwe-portal/web/components/auth/login-dialog';
import { useEnabledFlags } from '@wwe-portal/web/components/common/feature-flag';

interface UseExclusiveStoreDataProps {
  closeExclusiveDialog: () => void;
  purchaseItemsByClubCoins: (payload: { offerData: OfferData }) => void;
}
export const useExclusiveStoreData = ({
  closeExclusiveDialog,
  purchaseItemsByClubCoins,
}: UseExclusiveStoreDataProps) => {
  const t = useTranslations();
  const {
    enabled: [premiumOfferEnabled, prestigeOfferEnabled, superVIPEnabled],
  } = useEnabledFlags([
    'clubStore.offers.premium',
    'clubStore.offers.prestige',
    'clubStore.offers.super_vip',
  ]);

  const isAuthenticated = useIsAuthenticated();
  const { amount: currentUserClubCoin } = useGetClubCoins();
  const { currentPlan, isLoading: isLoadingSubscription } =
    useSubscriptionQuery();
  const { data: exclusiveItemResponse, isLoading: isLoadingExclusiveItems } =
    useExclusiveItems();
  const getValidationPurchase = (data: {
    tier: SubscriptionTier | undefined;
    offerData: OfferData;
    onFailedCallback?: () => void;
  }) => {
    const { tier } = data;

    // if user is not logged in
    if (!isAuthenticated) {
      closeExclusiveDialog();
      showDialog(LoginDialogPortable, {
        title: 'Login To Subscribe',
      });
      return;
    }

    // if user did not subscribe a plan
    if (!currentPlan) {
      closeExclusiveDialog();
      showDialog(ReferSubscriptionDialog, {
        description: (
          <>
            Please go to subscription page
            <br />
            subscribe the {subscriptionMap[tier!].name} plan
          </>
        ),
      });
      return;
    }

    // if user subscribed a lower plan
    if (isBetterTier(data, currentPlan)) {
      closeExclusiveDialog();
      showDialog(ReferSubscriptionDialog, {
        description: (
          <>
            Please go to subscription page
            <br />
            and subscribe higher plan
            <br />
            to purchase items
          </>
        ),
      });
      return;
    }

    // if user does not have enough club coins
    if (
      (data.offerData.AlternativeCostAmount ?? 0) > (currentUserClubCoin ?? 0)
    ) {
      closeExclusiveDialog();
      showDialog(NotEnoughClubCoinDialog);
      return;
    }

    const offerData = exclusiveItemResponse?.find(
      (item) => item.offerData.OffersID === data.offerData.OffersID
    )?.offerData;

    if (offerData == null) return;

    purchaseItemsByClubCoins({
      offerData,
    });
  };
  const titles = useMemo(
    () => ({
      [SubscriptionTier.BRONZE]: t('clubStore_exclusive_BRONZE_title'),
      [SubscriptionTier.SILVER]: t('clubStore_exclusive_SILVER_title'),
      [SubscriptionTier.GOLD]: t('clubStore_exclusive_GOLD_title'),
    }),
    [t]
  );

  const enabled = useMemo(
    () => ({
      [SubscriptionTier.BRONZE]: Boolean(premiumOfferEnabled),
      [SubscriptionTier.SILVER]: Boolean(prestigeOfferEnabled),
      [SubscriptionTier.GOLD]: Boolean(superVIPEnabled),
    }),
    [premiumOfferEnabled, prestigeOfferEnabled, superVIPEnabled]
  );
  const descriptions = useMemo(
    () => ({
      [SubscriptionTier.BRONZE]: t('clubStore_exclusive_BRONZE_description'),
      [SubscriptionTier.SILVER]: t('clubStore_exclusive_SILVER_description'),
      [SubscriptionTier.GOLD]: t('clubStore_exclusive_GOLD_description'),
    }),
    [t]
  );

  const isLoading = isLoadingSubscription || isLoadingExclusiveItems;

  return {
    titles,
    descriptions,
    enabled,
    getValidationPurchase,
    currentPlan,
    exclusiveItemResponse,
    isLoading,
  };
};
